let loadPromise = null;

/**
 * Loads the Google Maps JavaScript API once (with the Places library) and resolves
 * with the global `google` namespace. Rejects when no api key is provided.
 */
export function loadGoogleMaps(apiKey, language = "ru") {
    if (!apiKey) {
        return Promise.reject(new Error("Missing Google Maps API key"));
    }

    if (window.google && window.google.maps) {
        return Promise.resolve(window.google);
    }

    if (!loadPromise) {
        loadPromise = new Promise((resolve, reject) => {
            const callbackName = "__abioInitGoogleMaps__";
            window[callbackName] = () => resolve(window.google);

            const script = document.createElement("script");
            script.src =
                `https://maps.googleapis.com/maps/api/js?key=${apiKey}` +
                `&libraries=places&language=${language}&callback=${callbackName}`;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                loadPromise = null;
                reject(new Error("Failed to load Google Maps script"));
            };
            document.head.appendChild(script);
        });
    }

    return loadPromise;
}

// City names for Yerevan across the supported locales (en / hy / ru).
const YEREVAN_NAMES = ["yerevan", "երևան", "ереван"];

/**
 * Determines whether Google address components point to Yerevan.
 * Checks the administrative area and locality against the localized city names.
 */
export function isYerevan(components) {
    if (!Array.isArray(components)) return false;
    return components.some((component) => {
        const types = component.types || [];
        if (!types.includes("administrative_area_level_1") && !types.includes("locality")) {
            return false;
        }
        const names = [component.long_name, component.short_name]
            .filter(Boolean)
            .map((name) => name.toLowerCase());
        return names.some((name) => YEREVAN_NAMES.some((city) => name.includes(city)));
    });
}

/**
 * Geocodes a free-form address into {lat, lng, formatted, components}.
 * `region` restricts results to a country (ISO code, e.g. "am" for Armenia).
 * Returns null when nothing is found.
 */
export function geocodeAddress(google, address, region = "am") {
    return new Promise((resolve) => {
        if (!address) return resolve(null);
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {address, componentRestrictions: {country: region}},
            (results, status) => {
                if (status === "OK" && results && results[0]) {
                    const loc = results[0].geometry.location;
                    resolve({
                        lat: loc.lat(),
                        lng: loc.lng(),
                        formatted: results[0].formatted_address,
                        components: results[0].address_components,
                    });
                } else {
                    resolve(null);
                }
            }
        );
    });
}

/**
 * Driving distance (in meters) between two {lat, lng} points, using the Distance Matrix service.
 */
export function getDrivingDistanceMeters(google, origin, destination) {
    return new Promise((resolve, reject) => {
        const service = new google.maps.DistanceMatrixService();
        service.getDistanceMatrix(
            {
                origins: [origin],
                destinations: [destination],
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (response, status) => {
                if (status !== "OK") {
                    return reject(new Error("DistanceMatrix status: " + status));
                }
                const element = response.rows?.[0]?.elements?.[0];
                if (!element || element.status !== "OK") {
                    return reject(new Error("DistanceMatrix element status: " + (element?.status || "NONE")));
                }
                resolve(element.distance.value);
            }
        );
    });
}

/**
 * Reverse-geocodes {lat, lng} coordinates into {formatted, components}.
 * Returns null when nothing is found.
 */
export function reverseGeocode(google, latLng) {
    return new Promise((resolve) => {
        if (!latLng) return resolve(null);
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({location: latLng}, (results, status) => {
            if (status === "OK" && results && results[0]) {
                resolve({
                    formatted: results[0].formatted_address,
                    components: results[0].address_components,
                });
            } else {
                resolve(null);
            }
        });
    });
}

/**
 * Requests a driving route between two {lat, lng} points using the Directions service.
 * Resolves with {meters, result} where `result` can be fed to a DirectionsRenderer.
 * Rejects when the Directions API is unavailable/not enabled, so callers can fall back.
 */
export function getDrivingRoute(google, origin, destination) {
    return new Promise((resolve, reject) => {
        const service = new google.maps.DirectionsService();
        service.route(
            {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                if (status === "OK" && result.routes && result.routes[0]) {
                    const meters = result.routes[0].legs.reduce(
                        (sum, leg) => sum + (leg.distance?.value || 0),
                        0
                    );
                    resolve({meters, result});
                } else {
                    reject(new Error("Directions status: " + status));
                }
            }
        );
    });
}

/**
 * Best-effort address autocomplete on an input element.
 * Uses the classic Places Autocomplete widget when available; returns null otherwise
 * so callers can fall back to geocoding on blur.
 * `onSelect` receives {formatted, lat, lng}.
 */
export function attachAutocomplete(google, input, onSelect, region = "am") {
    try {
        if (!google.maps.places || !google.maps.places.Autocomplete) return null;
        const autocomplete = new google.maps.places.Autocomplete(input, {
            componentRestrictions: {country: region},
            fields: ["geometry", "formatted_address", "address_components"],
        });
        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (place && place.geometry) {
                const loc = place.geometry.location;
                onSelect({
                    formatted: place.formatted_address,
                    lat: loc.lat(),
                    lng: loc.lng(),
                    components: place.address_components,
                });
            }
        });
        return autocomplete;
    } catch (e) {
        console.log("Google Autocomplete unavailable:", e);
        return null;
    }
}
